import { supabase, handleSupabaseError } from '@/lib/supabase';
import { StoreCustomer, CustomerSearchResult } from '@/types/models/Customer';
import { logger } from '@/lib/logger';
import { LLMService } from '@/services/ai';

export class CustomerService {
  static async search(storeId: string, query: string): Promise<CustomerSearchResult[]> {
    try {
      logger.info('Searching customers', { storeId, query });

      const { data, error } = await supabase
        .from('store_customers')
        .select('id, customer_name, customer_name_locale, customer_phone, created_at, store_id, is_active')
        .eq('store_id', storeId)
        .or(`customer_name.ilike.%${query}%,customer_phone.ilike.%${query}%`)
        .order('customer_name')
        .limit(10);

      if (error) {
        logger.error('Failed to search customers', { error, storeId, query });
        throw error;
      }

      return data || [];
    } catch (error) {
      logger.error('Error in search', { error, storeId, query });
      throw handleSupabaseError(error);
    }
  }

  static async getByPhone(storeId: string, phone: string): Promise<StoreCustomer | null> {
    try {
      logger.info('Finding customer by phone', { storeId, phone });

      const { data, error } = await supabase
        .from('store_customers')
        .select('*')
        .eq('store_id', storeId)
        .eq('customer_phone', phone)
        .maybeSingle();

      if (error) {
        logger.error('Failed to find customer by phone', { error, storeId, phone });
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error in findByPhone', { error, storeId, phone });
      throw handleSupabaseError(error);
    }
  }

  static async update(id: string, data: {
    customerName?: string;
    language_locale?: string;
  }): Promise<StoreCustomer> {
    try {
      logger.info('Updating customer', { id, data });     
      let customerNameLocale: string | null = null;
      const updateData: any = {};
      if (data.customerName) {
        try {
          customerNameLocale = await LLMService.translate(data.customerName, data.language_locale || 'hindi');
          logger.debug('Translated customer name for update', { 
            original: data.customerName, 
            translated: customerNameLocale, 
            language: data.language_locale 
          });
        } catch (err) {
          logger.warn('Failed to translate customer name for update, using original', { error: err });
          customerNameLocale = data.customerName;
        }
        updateData.customer_name = data.customerName;
        updateData.customer_name_locale = customerNameLocale;
      }
      // Add more fields to updateData here if needed in the future
      if (Object.keys(updateData).length === 0) {
        throw new Error('No valid fields provided to update');
      }
      const { data: customer, error } = await supabase
        .from('store_customers')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        logger.error('Failed to update customer', { error, id, data });
        throw error;
      }
      if (!customer) {
        logger.error('Customer not found during update', { id });
        throw new Error(`Customer not found with ID: ${id}`);
      }
      logger.debug('Customer updated successfully', { id });
      return customer;
    } catch (error) {
      logger.error('Error in update', { error, id, data });
      throw handleSupabaseError(error);
    }
  }

  static async create(data: {
    storeId: string;
    customerName: string;
    customerPhone: string;
    updateName: boolean;
    language_locale?: string;
  }): Promise<StoreCustomer> {
    try {
      logger.info('Creating customer', { data });

      // First check if customer exists
      const existingCustomer = await this.getByPhone(data.storeId, data.customerPhone);
      if (existingCustomer) {
        logger.info('Customer exists, updating name', { 
          customerId: existingCustomer.id,
          oldName: existingCustomer.customer_name,
          newName: data.customerName
        });
        
        // If customer exists but name is different, update the name
        if ( data.updateName && existingCustomer.customer_name !== data.customerName) {
          const updateData: { customerName: string; language_locale?: string } = {
            customerName: data.customerName
          };
          if (data.language_locale) {
            updateData.language_locale = data.language_locale;
          }
          return this.update(existingCustomer.id, updateData);
        }
        
        return existingCustomer;
      }

      // Get store's language_locale if not provided
      let language_locale = data.language_locale;
      if (!language_locale) {
        try {
          const { data: store } = await supabase
            .from('stores')
            .select('language_locale')
            .eq('id', data.storeId)
            .single();
          language_locale = store?.language_locale || 'English';
        } catch (err) {
          logger.warn('Could not fetch store language, defaulting to English', { error: err });
          language_locale = 'English';
        }
      }

      // Get local name using LLMService with store's language
      let customerNameLocale: string | null = null;
      try {
        customerNameLocale = await LLMService.translate(data.customerName, language_locale || 'English');
        logger.debug('Translated customer name', { 
          original: data.customerName, 
          translated: customerNameLocale, 
          language: language_locale 
        });
      } catch (err) {
        logger.warn('Failed to translate customer name, using original', { error: err });
        customerNameLocale = null;
      }

      const { data: customer, error } = await supabase
        .from('store_customers')
        .insert([{
          store_id: data.storeId,
          customer_name: data.customerName,
          customer_name_locale: customerNameLocale,
          customer_phone: data.customerPhone,
        }])
        .select()
        .single();

      if (error) {
        logger.error('Failed to create customer', { error, data });
        throw error;
      }

      return customer;
    } catch (error) {
      logger.error('Error in create', { error, data });
      throw handleSupabaseError(error);
    }
  }

  static async getById(storeId: string, customerId: string): Promise<StoreCustomer | null> {
    try {
      logger.info('Getting customer by ID', { storeId, customerId });

      const { data, error } = await supabase
        .from('store_customers')
        .select('*')
        .eq('store_id', storeId)
        .eq('id', customerId)
        .single();

      if (error) {
        logger.error('Failed to get customer by ID', { error, storeId, customerId });
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error in getById', { error, storeId, customerId });
      throw handleSupabaseError(error);
    }
  }

  static async getAll(storeId: string): Promise<StoreCustomer[]> {
    try {
      logger.info('Fetching all customers', { storeId });
      const { data, error } = await supabase
        .from('store_customers')
        .select('*, created_at')
        .eq('store_id', storeId)
        .order('customer_name');
      if (error) {
        logger.error('Failed to fetch all customers', { error, storeId });
        throw error;
      }
      return data || [];
    } catch (error) {
      logger.error('Error in getAll', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  static async delete(storeId: string, id: string): Promise<void> {
    try {
      logger.info('Deleting customer', { storeId, id });
      const { error } = await supabase
        .from('store_customers')
        .delete()
        .eq('store_id', storeId)
        .eq('id', id);
      if (error) {
        logger.error('Failed to delete customer', { error, storeId, id });
        throw error;
      }
    } catch (error) {
      logger.error('Error in delete', { error, storeId, id });
      throw handleSupabaseError(error);
    }
  }

  static async toggleActive(storeId: string, id: string, isActive: boolean): Promise<StoreCustomer> {
    try {
      logger.info('Toggling customer active status', { storeId, id, isActive });
      
      const { data: customer, error } = await supabase
        .from('store_customers')
        .update({ is_active: isActive })
        .eq('store_id', storeId)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('Failed to toggle customer active status', { error, storeId, id, isActive });
        throw error;
      }

      if (!customer) {
        logger.error('Customer not found during toggle active', { id });
        throw new Error(`Customer not found with ID: ${id}`);
      }

      logger.debug('Customer active status toggled successfully', { id, isActive });
      return customer;
    } catch (error) {
      logger.error('Error in toggleActive', { error, storeId, id, isActive });
      throw handleSupabaseError(error);
    }
  }


}